import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  ReactNode,
  KeyboardEvent,
} from "react";
import { Annotations, api, clock, Peaks, Spectrum } from "../api";
import { useApp } from "./context";
import { audioMap } from "./timeline";
import { clampView, timeAtFraction, waveformBins, zoomView } from "./spectrum";

const colours = [
  [5, 12, 23],
  [18, 35, 66],
  [19, 82, 107],
  [39, 148, 155],
  [169, 196, 124],
  [247, 153, 66],
  [255, 239, 202],
];
function colour(value: number) {
  const at = (value / 255) * (colours.length - 1),
    i = Math.min(colours.length - 2, Math.floor(at)),
    f = at - i;
  return colours[i].map((v, c) => Math.round(v + (colours[i + 1][c] - v) * f));
}
const palette = Array.from({ length: 256 }, (_, n) => colour(n));
export default function Spectrogram({
  recordingId,
  duration,
  ms,
  peaks,
  annotations,
  onSeek,
  children,
}: {
  recordingId?: string;
  duration: number;
  ms: number;
  peaks: Peaks | null;
  annotations: Annotations | null;
  onSeek: (ms: number) => void;
  children?: ReactNode;
}) {
  const { t, appearance } = useApp();
  const [spectral, setSpectral] = useState(false);
  const playedClip = useId();
  const [view, setView] = useState(() => clampView(0, duration, duration));
  const [following, setFollowing] = useState(appearance.followPlayback);
  const [data, setData] = useState<Spectrum | null>(null),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(false);
  const [cursor, setCursor] = useState<{
    time: number;
    hz: number;
    db: number | null;
  } | null>(null);
  const [scale, setScale] = useState("log"),
    [limit, setLimit] = useState(8000);
  const canvas = useRef<HTMLCanvasElement>(null),
    plot = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    x: number;
    start: number;
    span: number;
    width: number;
    moved: boolean;
  } | null>(null);
  const map = useMemo(
    () => audioMap(annotations, duration),
    [annotations, duration],
  );
  const samples = useMemo(
    () =>
      data ? Uint8Array.from(atob(data.values), (c) => c.charCodeAt(0)) : null,
    [data],
  );
  const maximum = Math.min(limit, (data?.sample_rate || 16000) / 2);
  const minimum = scale === "log" ? Math.min(31.25, maximum / 4) : 0;
  const frequency = (fraction: number) =>
    scale === "log"
      ? minimum * Math.pow(maximum / minimum, 1 - fraction)
      : maximum * (1 - fraction);
  const y = (hz: number) =>
    scale === "log"
      ? 1 - Math.log(hz / minimum) / Math.log(maximum / minimum)
      : 1 - hz / maximum;
  const zoom = Math.max(1, duration / view.span);
  const showHead = ms >= view.start && ms <= view.start + view.span;
  const head = Math.max(0, Math.min(1, (ms - view.start) / view.span));
  const waveform = useMemo(() => waveformBins(peaks, view, 320), [peaks, view]);
  function seekAt(clientX: number, element: HTMLElement) {
    const r = element.getBoundingClientRect();
    onSeek(timeAtFraction(view, (clientX - r.left) / r.width, duration));
  }
  function seekKey(e: KeyboardEvent<HTMLElement>) {
    if (
      ![
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ].includes(e.key)
    )
      return;
    e.preventDefault();
    e.stopPropagation();
    onSeek(
      e.key === "Home"
        ? 0
        : e.key === "End"
          ? duration
          : Math.max(
              0,
              Math.min(
                duration,
                ms + (["ArrowRight", "ArrowUp"].includes(e.key) ? 5000 : -5000),
              ),
            ),
    );
  }
  function zoomBy(
    factor: number,
    anchor = showHead ? ms : view.start + view.span / 2,
  ) {
    setView((v) => zoomView(v, factor, anchor, duration));
  }
  useEffect(() => {
    setView(clampView(0, duration, duration));
    setData(null);
  }, [recordingId, duration]);
  useEffect(() => {
    if (!following || drag.current) return;
    setView((v) =>
      ms < v.start || ms > v.start + v.span * 0.95
        ? clampView(ms - v.span * 0.2, v.span, duration)
        : v,
    );
  }, [ms, following, duration]);
  useEffect(() => {
    if (!spectral || !recordingId || !duration) return;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      api
        .spectrum(
          recordingId,
          view.start,
          view.start + view.span,
          controller.signal,
        )
        .then((value) => {
          if (!controller.signal.aborted) setData(value);
        })
        .catch((e) => {
          if (e.name !== "AbortError") setError(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 140);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [spectral, recordingId, view.start, view.span, duration]);
  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    const draw = () => {
      const ctx = element.getContext("2d");
      if (!ctx) return;
      const width = Math.max(
          1,
          Math.round(element.clientWidth * devicePixelRatio),
        ),
        height = Math.max(
          1,
          Math.round(element.clientHeight * devicePixelRatio),
        );
      element.width = width;
      element.height = height;
      ctx.fillStyle = "#050c17";
      ctx.fillRect(0, 0, width, height);
      if (!data || !samples) return;
      const raster = document.createElement("canvas");
      raster.width = data.columns;
      raster.height = 256;
      const source = raster.getContext("2d")!;
      const pixels = source.createImageData(raster.width, raster.height);
      for (let row = 0; row < 256; row++) {
        const bin = Math.min(
          data.bins - 1,
          Math.round((frequency(row / 255) / data.sample_rate) * data.fft_size),
        );
        for (let col = 0; col < data.columns; col++) {
          const value = samples[bin * data.columns + col],
            rgb = palette[value],
            i = (row * data.columns + col) * 4;
          pixels.data[i] = rgb[0];
          pixels.data[i + 1] = rgb[1];
          pixels.data[i + 2] = rgb[2];
          pixels.data[i + 3] = 255;
        }
      }
      source.putImageData(pixels, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(
        raster,
        ((data.start_ms - view.start) / view.span) * width,
        0,
        ((data.end_ms - data.start_ms) / view.span) * width,
        height,
      );
    };
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(element);
    return () => observer.disconnect();
  }, [spectral, data, samples, view.start, view.span, scale, maximum]);
  useEffect(() => {
    const el = plot.current;
    if (!el) return;
    const wheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const r = el.getBoundingClientRect();
        setView((v) =>
          zoomView(
            v,
            Math.exp(-event.deltaY * 0.008),
            v.start + ((event.clientX - r.left) / r.width) * v.span,
            duration,
          ),
        );
        setFollowing(false);
      } else if (
        event.shiftKey ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ) {
        event.preventDefault();
        setFollowing(false);
        setView((v) =>
          clampView(
            v.start +
              ((event.deltaX || event.deltaY) / el.clientWidth) * v.span,
            v.span,
            duration,
          ),
        );
      }
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, [duration]);
  const ticks =
    scale === "log"
      ? [
          ...new Set([
            maximum,
            ...[8000, 2000, 500, 125, 31.25].filter(
              (hz) => hz < maximum && hz > minimum,
            ),
            minimum,
          ]),
        ]
      : Array.from({ length: 5 }, (_, i) => maximum * (1 - i / 4));
  const overview = useMemo(
    () =>
      Array.from({ length: 240 }, (_, i) => {
        const values = peaks?.peaks || [],
          start = Math.floor((i * values.length) / 240),
          end = Math.max(
            start + 1,
            Math.floor(((i + 1) * values.length) / 240),
          );
        let peak = 0;
        for (let n = start; n < end; n++) peak = Math.max(peak, values[n] || 0);
        return peak / 255;
      }),
    [peaks],
  );
  return (
    <>
      <div
        className={`spectral-instrument ${spectral ? "frequency-view" : "waveform-view"}`}
      >
        <div className="spectral-toolbar">
          <button
            className="spectral-toggle"
            aria-label={t("frequencyView")}
            title={t("frequencyView")}
            aria-pressed={spectral}
            onClick={() => setSpectral(!spectral)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              aria-hidden="true"
            >
              <path d="M3 8v8m4-12v16m5-13v10m5-14v18m4-12v6" />
              <path d="M2 12h20" opacity=".4" />
            </svg>
            <span>{t("frequencyView")}</span>
          </button>
          <output className="spectral-readout">
            {cursor
              ? `${clock(cursor.time)}${spectral ? ` · ${Math.round(cursor.hz)} Hz${cursor.db !== null ? ` · ${cursor.db.toFixed(1)} dBFS` : ""}` : ""}`
              : spectral
                ? "Hz · dBFS"
                : ""}
          </output>
          <div className="spectral-zoom" role="group" aria-label={t("zoom")}>
            <button
              aria-label={t("zoomOut")}
              title={t("zoomOut")}
              disabled={!duration || zoom <= 1.001}
              onClick={() => zoomBy(0.5)}
            >
              −
            </button>
            <output aria-label={t("zoom")}>
              {zoom.toFixed(zoom < 10 ? 1 : 0)}×
            </output>
            <button
              aria-label={t("zoomIn")}
              title={t("zoomIn")}
              disabled={!duration || view.span <= Math.min(5000, duration)}
              onClick={() => zoomBy(2)}
            >
              +
            </button>
            <button
              onClick={() => {
                setView(clampView(0, duration, duration));
                setFollowing(true);
              }}
            >
              {t("fit")}
            </button>
            <button
              aria-label={t("follow")}
              aria-pressed={following}
              onClick={() => {
                setFollowing(!following);
                if (!following)
                  setView((v) =>
                    clampView(ms - v.span * 0.3, v.span, duration),
                  );
              }}
            >
              {t("followShort")}
            </button>
          </div>
        </div>
        <div className="spectral-grid" dir="ltr">
          <div className="frequency-axis" aria-hidden="true">
            {spectral &&
              ticks.map((hz) => (
                <span key={hz} style={{ top: `${y(hz) * 100}%` }}>
                  {hz >= 1000 ? `${hz / 1000}k` : Math.round(hz)}
                </span>
              ))}
          </div>
          <div
            className="spectral-plot"
            ref={plot}
            role="slider"
            tabIndex={0}
            aria-label={t("seek")}
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(ms)}
            aria-valuetext={`${clock(ms)} / ${clock(duration)}`}
            aria-describedby="spectrum-help"
            onKeyDown={(e) => {
              if (["+", "=", "-"].includes(e.key)) {
                e.preventDefault();
                e.stopPropagation();
                zoomBy(e.key === "-" ? 0.5 : 2);
              } else if (
                e.shiftKey &&
                ["ArrowLeft", "ArrowRight"].includes(e.key)
              ) {
                e.preventDefault();
                e.stopPropagation();
                setFollowing(false);
                setView((v) =>
                  clampView(
                    v.start + (e.key === "ArrowRight" ? 1 : -1) * v.span * 0.2,
                    v.span,
                    duration,
                  ),
                );
              } else seekKey(e);
            }}
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              const r = e.currentTarget.getBoundingClientRect();
              drag.current = {
                x: e.clientX,
                start: view.start,
                span: view.span,
                width: r.width,
                moved: false,
              };
              e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect(),
                x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)),
                f = frequency(
                  Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)),
                ),
                time = view.start + x * view.span;
              let db = null;
              if (
                data &&
                samples &&
                time >= data.start_ms &&
                time <= data.end_ms
              ) {
                const col = Math.min(
                    data.columns - 1,
                    Math.floor(
                      ((time - data.start_ms) / (data.end_ms - data.start_ms)) *
                        data.columns,
                    ),
                  ),
                  bin = Math.min(
                    data.bins - 1,
                    Math.round((f / data.sample_rate) * data.fft_size),
                  );
                db =
                  data.min_db +
                  (samples[bin * data.columns + col] / 255) *
                    (data.max_db - data.min_db);
              }
              setCursor({ time, hz: f, db });
              if (drag.current && Math.abs(e.clientX - drag.current.x) > 5) {
                drag.current.moved = true;
                setFollowing(false);
                setView(
                  clampView(
                    drag.current.start -
                      ((e.clientX - drag.current.x) / drag.current.width) *
                        drag.current.span,
                    drag.current.span,
                    duration,
                  ),
                );
              }
            }}
            onPointerUp={(e) => {
              const d = drag.current;
              drag.current = null;
              if (!d) return;
              if (!d.moved) seekAt(e.clientX, e.currentTarget);
              e.currentTarget.releasePointerCapture(e.pointerId);
            }}
            onPointerCancel={() => {
              drag.current = null;
            }}
            onPointerLeave={() => setCursor(null)}
          >
            {spectral ? (
              <canvas ref={canvas} aria-hidden="true" />
            ) : (
              <svg
                className="listening-waveform"
                viewBox="0 0 960 160"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <defs>
                  <clipPath id={playedClip}>
                    <rect width={head * 960} height="160" />
                  </clipPath>
                </defs>
                <path className="waveform-baseline" d="M0 80H960" />
                <g>
                  {waveform.map((v, i) => (
                    <rect
                      key={i}
                      x={i * 3}
                      y={80 - v * 52}
                      width="2"
                      height={Math.max(1, v * 104)}
                      rx="1"
                    />
                  ))}
                </g>
                <g className="waveform-played" clipPath={`url(#${playedClip})`}>
                  {waveform.map((v, i) => (
                    <rect
                      key={i}
                      x={i * 3}
                      y={80 - v * 52}
                      width="2"
                      height={Math.max(1, v * 104)}
                      rx="1"
                    />
                  ))}
                </g>
              </svg>
            )}
            <div className="spectral-graticule" aria-hidden="true">
              {spectral &&
                ticks.map((hz) => (
                  <i key={hz} style={{ top: `${y(hz) * 100}%` }} />
                ))}
              {[0, 0.25, 0.5, 0.75, 1].map((x) => (
                <b key={x} style={{ left: `${x * 100}%` }} />
              ))}
            </div>
            {showHead && (
              <div
                className="spectral-playhead"
                style={{ left: `${head * 100}%` }}
                aria-hidden="true"
              />
            )}
            {showHead && (
              <span
                className="playhead-time"
                style={{
                  left: `clamp(0px, calc(${head * 100}% - 2.9rem), calc(100% - 5.8rem))`,
                }}
                aria-hidden="true"
              >
                {clock(ms)}
              </span>
            )}
            {spectral && loading && (
              <span className="spectral-state" role="status">
                {t("loadingSpectrum")}
              </span>
            )}
            {spectral && (error || !recordingId) && (
              <span className="spectral-state" role="status">
                {t("spectrumUnavailable")}
              </span>
            )}
          </div>
          <span className="axis-unit">{spectral ? "Hz" : ""}</span>
          <div className="spectral-times">
            {[0, 0.25, 0.5, 0.75, 1].map((x, i) => (
              <span key={x} className={i === 1 || i === 3 ? "minor-time" : ""}>
                {clock(view.start + x * view.span)}
              </span>
            ))}
          </div>
          {(["speech", "music"] as const).map((kind) => (
            <div className={`spectral-lane spectral-${kind}`} key={kind}>
              <span>{t(kind === "speech" ? "speech" : "musicLabel")}</span>
              <div
                role="slider"
                tabIndex={0}
                aria-label={t(kind === "speech" ? "seekSpeech" : "seekMusic")}
                aria-valuemin={0}
                aria-valuemax={Math.round(duration)}
                aria-valuenow={Math.round(ms)}
                aria-valuetext={`${clock(ms)} / ${clock(duration)}`}
                onClick={(e) => seekAt(e.clientX, e.currentTarget)}
                onKeyDown={seekKey}
              >
                {map[kind]
                  .filter(
                    (r) =>
                      r.end_ms > view.start &&
                      r.start_ms < view.start + view.span,
                  )
                  .map((r, i) => (
                    <i
                      key={i}
                      style={{
                        left: `${Math.max(0, (r.start_ms - view.start) / view.span) * 100}%`,
                        width: `${((Math.min(r.end_ms, view.start + view.span) - Math.max(r.start_ms, view.start)) / view.span) * 100}%`,
                      }}
                      title={`${clock(r.start_ms)}–${clock(r.end_ms)}`}
                    />
                  ))}
                {showHead && (
                  <b
                    style={{
                      left: `${head * 100}%`,
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        {zoom > 1.001 && (
          <div className="spectral-overview" dir="ltr">
            <svg
              viewBox="0 0 960 36"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {overview.map((v, i) => (
                <rect
                  key={i}
                  x={i * 4}
                  y={18 - v * 16}
                  width="2"
                  height={Math.max(1, v * 32)}
                />
              ))}
              <rect
                className="overview-window"
                x={(view.start / Math.max(1, duration)) * 960}
                y="1"
                width={(view.span / Math.max(1, duration)) * 960}
                height="34"
              />
            </svg>
            <input
              type="range"
              min={0}
              max={Math.max(0, duration - view.span)}
              step={Math.max(1, view.span / 200)}
              value={view.start}
              disabled={!duration || zoom <= 1.001}
              aria-label={t("panTimeline")}
              aria-valuetext={`${clock(view.start)}–${clock(view.start + view.span)}`}
              onChange={(e) => {
                setFollowing(false);
                setView((v) => clampView(+e.target.value, v.span, duration));
              }}
            />
          </div>
        )}
      </div>
      {children}
      <details className="spectrum-details">
        <summary>{t(spectral ? "spectrumSettings" : "playerGuide")}</summary>
        {spectral && (
          <>
            <div className="spectrum-options">
              <label>
                {t("frequencyScale")}
                <select
                  value={scale}
                  onChange={(e) => setScale(e.target.value)}
                >
                  <option value="log">{t("logarithmic")}</option>
                  <option value="linear">{t("linear")}</option>
                </select>
              </label>
              <label>
                {t("frequencyRange")}
                <select
                  value={limit}
                  onChange={(e) => setLimit(+e.target.value)}
                >
                  {[8000, 4000, 2000, 1000].map((n) => (
                    <option key={n} value={n}>
                      {n / 1000} kHz
                    </option>
                  ))}
                </select>
              </label>
              <div className="spectrum-legend">
                <span>−90 dBFS</span>
                <i />
                <span>0 dBFS</span>
              </div>
            </div>
            <p>
              {data
                ? `${data.window} · ${data.fft_size} FFT · ${data.sample_rate / 1000} kHz · ${((data.fft_size / data.sample_rate) * 1000).toFixed(0)} ms`
                : "FFT"}{" "}
              — {t("spectrumAveraged")}
            </p>
          </>
        )}
        <p id="spectrum-help">{t("spectrumHelp")}</p>
        {spectral && (
          <output className="spectrum-cursor">
            {cursor
              ? `${clock(cursor.time)} · ${Math.round(cursor.hz)} Hz${cursor.db !== null ? ` · ${cursor.db.toFixed(1)} dBFS` : ""}`
              : t("spectrumCursor")}
          </output>
        )}
      </details>
    </>
  );
}
